import { FilterFacetLayout } from '@core/enums';
import ImageTransform from '@tti/image-transform';
import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { ProductDetailsContentStyles } from '../ProductDetailsContent/ProductDetailsContent.styles';
import { ProductDetailsContentVariantSelectorStyles as S } from './ProductDetailsContentVariantSelector.styles';

export enum VariantSelectionLayout {
  Cards = 0,
  AttributeFacets = 1,
}

export interface ProductDetailsContentVariantSelectorProps {
  translations: Record<string, string | undefined>;
  selectedVariant: SimpleProductVariantViewModel | undefined;
  variants: SimpleProductVariantViewModel[];
  layout: VariantSelectionLayout;
  onVariantSelected: (agilityId: number) => void;
}

interface FilterFacetValue {
  name: string;
  value: string;
  rawValue: string;
  image?: string;
  displayValue: string;
  disabled: boolean;
  hideSingleValue: boolean;
}

interface FilterFacet {
  id: string;
  name: string;
  values: FilterFacetValue[];
  layout: FilterFacetLayout;
}

const ProductDetailsContentVariantSelector: FunctionComponent<ProductDetailsContentVariantSelectorProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [selectedFacets, setSelectedFacets] = useState<Map<string, string>>(() => {
    if (!props.selectedVariant) {
      return new Map();
    }

    if (!props.selectedVariant.attributeFacets) {
      return new Map();
    }

    const initialFiltersFromVariant = new Map();

    for (const facet of props.selectedVariant.attributeFacets) {
      initialFiltersFromVariant.set(facet.id, facet.value);
    }

    return initialFiltersFromVariant;
  });
  const [lastSelectedFacet, setLastSelectedFacet] = useState<string | null>(null);
  const [trigger, setTrigger] = React.useState<number>(0);

  const longestValues = useRef<Record<string, string | undefined>>({});
  interface FractionMapping {
    [fraction: string]: number;
  }

  const orderValues = (a: string, b: string): number => {
    function fractionToDecimal(fraction: string): number {
      const specialFractions: FractionMapping = {
        '½': 0.5,
        '⅜': 3 / 8,
        '⅝': 5 / 8,
        '¾': 3 / 4,
        '¼': 1 / 4,
        '⅓': 1 / 3,
        '⅔': 2 / 3,
      };

      const [whole, special, numerator] = fraction.match(/(\d+)?(?:\s*([½⅜⅝¾¼⅓⅔]))?(?:\s*(\d+\/\d+))?/)?.slice(1) ?? [];
      const num = whole ? parseInt(whole, 10) : 0;
      if (special) {
        return num + specialFractions[special];
      }
      if (!numerator) {
        return num;
      }
      const [n, d] = numerator.split('/').map(Number);
      return num + n / d;
    }

    const decimalA = fractionToDecimal(a);
    const decimalB = fractionToDecimal(b);

    return decimalA - decimalB;
  };

  const initialFilters = useMemo(() => {
    const f = new Map<string, FilterFacet>();

    for (const variant of props.variants) {
      if (variant.attributeFacets === null) {
        continue;
      }

      for (const facet of variant.attributeFacets) {
        const existingFacet = f.get(facet.id);
        const longestValue = longestValues.current[facet.id];

        if (!longestValue || facet.value.length > longestValue.length) {
          longestValues.current[facet.id] = facet.value;
        }

        if (!existingFacet) {
          f.set(facet.id, {
            id: facet.id,
            name: facet.name,
            values: [
              {
                value: facet.value,
                rawValue: facet.rawValue,
                disabled: false,
                name: facet.name,
                image: facet.image,
                displayValue: facet.displayValue,
                hideSingleValue: facet.hideSingleValue,
              },
            ],
            layout: facet.layout,
          });
        } else if (existingFacet.values.findIndex((x) => x.value === facet.value) === -1) {
          existingFacet.values.push({
            value: facet.value,
            rawValue: facet.rawValue,
            disabled: false,
            name: facet.name,
            image: facet.image,
            displayValue: facet.displayValue,
            hideSingleValue: facet.hideSingleValue,
          });
        }
      }
    }

    return f;
  }, [props.variants]);

  const allFilters = useMemo(() => getAllPossibleFilterOptions(initialFilters, selectedFacets, props.variants), [
    initialFilters,
    selectedFacets,
    props.variants,
  ]);

  const handleSelectFacetValue = (facetId: string, value: string, disabled: boolean): void => {
    setLastSelectedFacet(facetId);
    setTrigger(new Date().getTime());

    let newSelectedFacets: Map<string, string> = new Map();

    if (!disabled) {
      newSelectedFacets = new Map(selectedFacets);
    }

    const existingValue = newSelectedFacets.get(facetId);

    if (existingValue === value) {
      newSelectedFacets.delete(facetId);
    } else {
      newSelectedFacets.set(facetId, value);
    }

    setSelectedFacets(newSelectedFacets);
  };

  useEffect(() => {
    if (props.selectedVariant?.isSet) {
      return;
    }

    const newSelectedFacets = new Map(selectedFacets);
    let edited = false;

    for (const facet of Array.from(allFilters.values())) {
      const notDisabledValues = facet.values.filter((x) => x.disabled !== true);

      if (notDisabledValues.length > 1 || notDisabledValues.length === 0) {
        continue;
      }

      if (lastSelectedFacet && facet.id === lastSelectedFacet) {
        continue;
      }

      if (newSelectedFacets.get(facet.id) !== notDisabledValues[0].value) {
        newSelectedFacets.set(facet.id, notDisabledValues[0].value);
        edited = true;
      }
    }

    if (edited) {
      setSelectedFacets(newSelectedFacets);
    }
  }, [Array.from(allFilters.values())]);

  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
      return;
    }

    if (selectedFacets.size == 0 && props.selectedVariant?.isSet) {
      return;
    }

    for (const variant of props.variants.filter((x) => !x.isSet)) {
      let matches = 0;

      if (!variant.attributeFacets) {
        continue;
      }

      for (const attributeFacet of variant.attributeFacets) {
        if (selectedFacets.get(attributeFacet.id) !== attributeFacet.value) {
          continue;
        }

        matches += 1;
      }

      if (matches === variant.attributeFacets.length) {
        props.onVariantSelected(variant.agilityId);
        return;
      } else {
        props.onVariantSelected(-1);
      }
    }
  }, [selectedFacets]);

  switch (props.layout) {
    default:
    case VariantSelectionLayout.Cards: {
      return (
        <ProductDetailsContentStyles.Section>
          <ProductDetailsContentStyles.H2>{props.translations['custom.selectmodel']}</ProductDetailsContentStyles.H2>
          <ProductDetailsContentStyles.H3>{props.selectedVariant?.name}</ProductDetailsContentStyles.H3>
          {props.selectedVariant?.articleNumberDisplayValues?.map((x) => (
            <ProductDetailsContentStyles.VariantDisplayName as="h4" x-ms-format-detection="none" key={x}>
              {x}
            </ProductDetailsContentStyles.VariantDisplayName>
          ))}
          <ProductDetailsContentStyles.Variants>
            {props.variants.map((x) => (
              <ProductDetailsContentStyles.VariantCard
                key={x.agilityId}
                isActive={x.agilityId === props.selectedVariant?.agilityId}
                onClick={() => props.onVariantSelected(x.agilityId)}
              >
                <img loading="lazy" src={new ImageTransform(x.image).width(64).url} alt={x.name} />
              </ProductDetailsContentStyles.VariantCard>
            ))}
          </ProductDetailsContentStyles.Variants>
        </ProductDetailsContentStyles.Section>
      );
    }

    case VariantSelectionLayout.AttributeFacets: {
      return (
        <>
          <ProductDetailsContentStyles.Section>
            <ProductDetailsContentStyles.H2>{props.translations['custom.selectmodel']}</ProductDetailsContentStyles.H2>
            <ProductDetailsContentStyles.H3>
              {props.selectedVariant ? props.selectedVariant.name : null}
            </ProductDetailsContentStyles.H3>
            {props.selectedVariant?.articleNumberDisplayValues?.map((x) => (
              <ProductDetailsContentStyles.VariantDisplayName as="h4" key={x}>
                {x}
              </ProductDetailsContentStyles.VariantDisplayName>
            ))}
          </ProductDetailsContentStyles.Section>
          <ProductDetailsContentStyles.Section>
            {Array.from(allFilters.values()).map((x) => {
              switch (x.layout) {
                case FilterFacetLayout.Buttons: {
                  if (x.values.length === 1 && x.values[0].hideSingleValue) {
                    return <></>;
                  }

                  return (
                    <>
                      <ProductDetailsContentStyles.H4 key={x.id}>{x.name}</ProductDetailsContentStyles.H4>

                      <S.Variants layout={x.layout}>
                        {x.values
                          .sort((a, b) => {
                            return orderValues(a.value, b.value);
                          })
                          .map((y) => {
                            const isActive = selectedFacets.get(x.id) === y.value;
                            const isDiabled = y.disabled && !isActive;

                            return (
                              <S.Button
                                key={y.value}
                                onClick={() => handleSelectFacetValue(x.id, y.value, isDiabled)}
                                data-longest-value={longestValues.current[x.id]}
                                isActive={isActive}
                                isDiabled={isDiabled}
                              >
                                <span dangerouslySetInnerHTML={{ __html: y.value }}></span>
                              </S.Button>
                            );
                          })}
                      </S.Variants>
                    </>
                  );
                }

                case FilterFacetLayout.Colours: {
                  const selectedFacetValue = selectedFacets.get(x.id);
                  const facet = allFilters.get(x.id);
                  let facetValue: string | undefined = undefined;

                  if (facet && selectedFacetValue) {
                    facetValue = facet.values.find((x) => x.value === selectedFacetValue)?.displayValue;
                  }

                  return (
                    <>
                      <ProductDetailsContentStyles.H4 key={x.id}>{x.name}</ProductDetailsContentStyles.H4>
                      <ProductDetailsContentStyles.FacetSelectedValue visible={!!facetValue}>
                        {props.translations['custom.productdetail.selectedcolour']}:{' '}
                        <span>{facetValue ? facetValue : null}</span>
                      </ProductDetailsContentStyles.FacetSelectedValue>

                      <S.Variants layout={x.layout}>
                        {x.values.map((y) => {
                          const isActive = selectedFacets.get(x.id) === y.value;
                          const isDiabled = y.disabled && !isActive;

                          return (
                            <S.Colour
                              key={y.value}
                              isActive={isActive}
                              isDiabled={isDiabled}
                              onClick={() => handleSelectFacetValue(x.id, y.value, isDiabled)}
                              title={y.value}
                            >
                              <S.ColourSquare style={{ backgroundColor: y.rawValue }}></S.ColourSquare>
                            </S.Colour>
                          );
                        })}
                      </S.Variants>
                    </>
                  );
                }

                case FilterFacetLayout.Thumbnails: {
                  const selectedFacetValue = selectedFacets.get(x.id);
                  const facet = allFilters.get(x.id);
                  let facetValue: string | undefined = undefined;

                  if (facet && selectedFacetValue) {
                    facetValue = facet.values.find((x) => x.value === selectedFacetValue)?.displayValue;
                  }

                  return (
                    <>
                      <ProductDetailsContentStyles.H4 key={x.id}>{x.name}</ProductDetailsContentStyles.H4>
                      <ProductDetailsContentStyles.FacetSelectedValue visible={!!facetValue}>
                        {props.translations['custom.productdetail.selectedcolour']}:{' '}
                        <span>{facetValue ? facetValue : null}</span>
                      </ProductDetailsContentStyles.FacetSelectedValue>

                      <S.Variants layout={x.layout}>
                        {x.values.map((y) => {
                          const isActive = selectedFacets.get(x.id) === y.value;
                          const isDisabled = y.disabled && !isActive;

                          return (
                            <S.Thumbnail
                              key={`${lastSelectedFacet}|${y.value}|${trigger}`}
                              isActive={isActive}
                              isDisabled={isDisabled}
                              onClick={() => handleSelectFacetValue(x.id, y.value, isDisabled)}
                              title={y.value}
                            >
                              {y.image ? (
                                <img loading="lazy" src={new ImageTransform(y.image).width(65).url} alt={y.name} />
                              ) : null}
                            </S.Thumbnail>
                          );
                        })}
                      </S.Variants>
                    </>
                  );
                }
              }
            })}

            {props.variants.some((x) => x.isSet) && (
              <S.AvailableAsSetWrapper>
                <ProductDetailsContentStyles.H4>
                  {props.translations['custom.productdetail.also-available-as-set']}
                </ProductDetailsContentStyles.H4>

                <S.Variants layout={FilterFacetLayout.Buttons}>
                  {props.variants
                    .filter((x) => x.isSet)
                    .map((x) => (
                      <S.AvailableAsSet
                        key={x.agilityId}
                        isActive={props.selectedVariant?.agilityId === x.agilityId}
                        onClick={() => {
                          props.onVariantSelected(props.selectedVariant?.agilityId === x.agilityId ? -1 : x.agilityId);
                          setSelectedFacets(new Map<string, string>());
                        }}
                      >
                        <img loading="lazy" src={new ImageTransform(x.image).width(64).url} alt={x.name} />
                      </S.AvailableAsSet>
                    ))}
                </S.Variants>
                {props.selectedVariant?.setIncludes && (
                  <>
                    <S.SetIncludesHeading>
                      {props.translations['custom.productdetail.set-includes']}
                    </S.SetIncludesHeading>
                    <S.SetIncludes>{props.selectedVariant.setIncludes}</S.SetIncludes>
                  </>
                )}
              </S.AvailableAsSetWrapper>
            )}
          </ProductDetailsContentStyles.Section>
        </>
      );
    }
  }
};

function applyFilters(
  filters: Map<string, string>,
  variants: SimpleProductVariantViewModel[],
): SimpleProductVariantViewModel[] {
  const filteredVariants: SimpleProductVariantViewModel[] = [];

  if (Array.from(filters).length === 0) {
    return variants;
  }

  for (const variant of variants) {
    let matchesFilterCount = 0;

    for (const filter of Array.from(filters)) {
      if (!variant.attributeFacets) {
        continue;
      }

      if (variant.attributeFacets.find((x) => x.id === filter[0] && x.value === filter[1]) === undefined) {
        continue;
      }

      matchesFilterCount += 1;
    }

    if (matchesFilterCount === Array.from(filters).length) {
      filteredVariants.push(variant);
    }
  }

  return filteredVariants;
}

function applyFilteringWithoutKey(
  key: string,
  filters: Map<string, string>,
  variants: SimpleProductVariantViewModel[],
): SimpleProductVariantViewModel[] {
  const filtersWithoutKey = new Map(filters);
  filtersWithoutKey.delete(key);

  return applyFilters(filtersWithoutKey, variants);
}

function getAllPossibleFilterOptions(
  filters: Map<string, FilterFacet>,
  activeFilters: Map<string, string>,
  variants: SimpleProductVariantViewModel[],
): Map<string, FilterFacet> {
  for (const filterFacet of Array.from(filters.values())) {
    const filteredVariants = applyFilteringWithoutKey(filterFacet.id, activeFilters, variants);

    const filter = filters.get(filterFacet.id);

    if (!filter) {
      continue;
    }

    for (const facet of filter.values) {
      const someVariantHasMatchingAttribute = filteredVariants.find((x) =>
        x.attributeFacets?.find((y) => y.id === filter.id && y.value === facet.value),
      );

      if (!someVariantHasMatchingAttribute) {
        facet.disabled = true;
        facet.image = variants.find((x) =>
          x.attributeFacets?.find((y) => y.id === filter.id && y.value === facet.value),
        )?.image;
      } else {
        facet.disabled = false;
        facet.image = someVariantHasMatchingAttribute.image;
      }
    }
  }

  return filters;
}

export default ProductDetailsContentVariantSelector;
